import React from 'react';
import { message } from 'antd';
import { useAtomValue, useSetAtom } from 'jotai';
import * as atoms from '@src/components/user-management/store/atom';
import { UserProfileEditor } from './UserProfileEditor';
import { BulkEditPermissions } from './BulkEditPermissions';
import {
  BulkEditOperationFunc,
  BulkInviteOperationFunc,
  BulkRevokeOperationFunc,
  EditOperationFunc,
} from '../types';
import { editUserSuccessMsg, State } from './utils';
import {
  useBulkEditPermissions,
  useEditUser,
  useInviteUsers,
  useRevokeUsers,
  useUpdateEditorState,
} from '../hooks';

export const ModalLayer = () => {
  const toInviteUser = useAtomValue(atoms.toInviteUserAtom);
  const userToEdit = useAtomValue(atoms.userToEditAtom);
  const usersToRevoke = useAtomValue(atoms.usersToRevokeAtom);
  const usersToBulkEdit = useAtomValue(atoms.usersToBulkEditAtom);
  const setAsyncLoadingMask = useSetAtom(atoms.asyncLoadingMaskAtom);
  const { resetUserEdit, resetUserInvite, resetUsersRevoke, resetUsersBulkEdit } =
    useUpdateEditorState();

  const inviteUsersMutation = useInviteUsers();
  const revokeUsersMutation = useRevokeUsers();
  const editUserMutation = useEditUser();
  const bulkEditMutation = useBulkEditPermissions();

  const handleInviteUser: BulkInviteOperationFunc = async (info) => {
    setAsyncLoadingMask(true);
    try {
      const resData = await inviteUsersMutation.mutateAsync(info);
      message.success(State.requestInviteSuccess);
      return resData;
    } catch (err: any) {
      // Try show error message responsed from server first
      message.error(err?.response?.data?.message ?? State.requestInviteFail);
      throw err;
    } finally {
      setAsyncLoadingMask(false);
    }
  };
  const handleRevokeUsers: BulkRevokeOperationFunc = async (info) => {
    setAsyncLoadingMask(true);
    try {
      const resData = await revokeUsersMutation.mutateAsync(info);
      message.success(State.requestRevokeSuccess);
      return resData;
    } catch (err: any) {
      // Try show error message responsed from server first
      message.error(err?.response?.data?.message ?? State.requestRevokeFail);
      throw err;
    } finally {
      setAsyncLoadingMask(false);
    }
  };

  const handleBulkEdit: BulkEditOperationFunc = async (info) => {
    setAsyncLoadingMask(true);
    try {
      const resData = await bulkEditMutation.mutateAsync(info);
      message.success(State.requestBulkEditSuccess);
      return resData;
    } catch (err: any) {
      // Try show error message responsed from server first
      message.error(err?.response?.data?.message ?? State.requestBulkEditFail);
      throw err;
    } finally {
      setAsyncLoadingMask(false);
    }
  };

  const handleEditUser: EditOperationFunc = async (info) => {
    setAsyncLoadingMask(true);
    try {
      const resData = await editUserMutation.mutateAsync(info);
      if (resData) {
        message.success(editUserSuccessMsg(info?.props.email));
        resetUserEdit();
      }
      return resData;
    } catch (err: any) {
      // Try show error message responsed from server first
      message.error(err?.response?.data?.message ?? State.requestEditFail);
    } finally {
      setAsyncLoadingMask(false);
    }
  };

  if (toInviteUser) {
    return (
      <UserProfileEditor action="invite" onCreate={handleInviteUser} onCancel={resetUserInvite} />
    );
  }
  if (usersToRevoke) {
    return (
      <UserProfileEditor
        action="revoke"
        usersToRevoke={usersToRevoke}
        onRevoke={handleRevokeUsers}
        onCancel={resetUsersRevoke}
      />
    );
  }

  if (userToEdit) {
    return (
      <UserProfileEditor
        action="edit"
        profile={userToEdit}
        onEdit={handleEditUser}
        onCancel={resetUserEdit}
      />
    );
  }

  if (usersToBulkEdit) {
    return (
      <BulkEditPermissions
        usersToBulkEdit={usersToBulkEdit}
        onBulkEdit={handleBulkEdit}
        onCancel={resetUsersBulkEdit}
      />
    );
  }

  return null;
};
